/**
 * Cold Start Bootstrap — Sprint 17f Task 3.10
 *
 * Ensures new users with sparse data see cards immediately, not an empty feed.
 *
 * Four layers:
 *   1. Onboarding seeds: category selectors → provisional entities
 *   2. Aggressive first-run: lower detection thresholds when < 3 entities
 *   3. Quick wins: per-data-source rules (top merchant → shopping, next event → restaurant)
 *   4. Progressive disclosure: detection confidence indicator
 */
import { makeLoveEntityId, emptySourceBreakdown, emptyProxyBreakdown } from '../types/love-entity';
// ---------------------------------------------------------------------------
// 1. Onboarding Seeds
// ---------------------------------------------------------------------------
/**
 * Create provisional entities from user's onboarding category selections.
 * These have low confidence (0.4) and zero stability — they'll be overridden
 * when real signal data arrives.
 */
export function createOnboardingSeeds(categories) {
    const now = Date.now();
    return categories.map(category => ({
        id: makeLoveEntityId('thing', category),
        type: 'thing',
        name: category,
        category,
        subcategories: [],
        confidence: 0.4,
        lastValidatedAt: now,
        firstSeenAt: now,
        lastSignalAt: now,
        signals: [{
                source: 'browsing',
                proxy: 'initiative',
                summary: `Selected "${category}" during onboarding`,
                strength: 0.4,
                timestamp: now,
            }],
        sourceBreakdown: emptySourceBreakdown(),
        proxyBreakdown: emptyProxyBreakdown(),
        trend: 'new',
        stability: 0,
        actionableContext: { suggestedActions: [], relatedEntityIds: [] },
        reasoning: 'Onboarding selection',
        proxyEvidence: {},
        ikigaiRelevance: { love: 0.5, goodAt: 0, worldNeeds: 0, paidFor: 0 },
        pass1Model: '',
        pass2Model: '',
        detectionRunId: 'onboarding',
    }));
}
const NORMAL_THRESHOLDS = {
    confidenceThreshold: 0.5,
    minSignals: 2,
};
const FIRST_RUN_THRESHOLDS = {
    confidenceThreshold: 0.3,
    minSignals: 1,
};
/**
 * When fewer than 3 entities exist, lower detection thresholds to be more
 * aggressive about finding interests from sparse data.
 */
export function getFirstRunThresholds(existingEntityCount) {
    return existingEntityCount < 3 ? FIRST_RUN_THRESHOLDS : NORMAL_THRESHOLDS;
}
/**
 * Per-data-source rules that generate immediate opportunities without
 * waiting for the full entity detection pipeline.
 */
export function getQuickWinOpportunities(sources) {
    const opportunities = [];
    const now = Date.now();
    // Top recurring merchant → shopping opportunity
    if (sources.financial?.topMerchant) {
        opportunities.push({
            entityName: sources.financial.topMerchant,
            description: `Quick win: top merchant ${sources.financial.topMerchant} (${sources.financial.transactionCount} transactions)`,
            agentType: 'shopping',
            suggestedMission: `Find deals at ${sources.financial.topMerchant}`,
            context: { source: 'cold_start', quickWinType: 'top_merchant' },
            createdAt: now,
            status: 'pending',
        });
    }
    // Next calendar event → restaurant/events opportunity
    if (sources.calendar?.nextEventTitle) {
        opportunities.push({
            entityName: sources.calendar.nextEventTitle,
            description: `Quick win: next event "${sources.calendar.nextEventTitle}" on ${sources.calendar.nextEventDate}`,
            agentType: 'restaurant',
            suggestedMission: `Find dining for ${sources.calendar.nextEventTitle}`,
            context: { source: 'cold_start', quickWinType: 'next_event' },
            createdAt: now,
            status: 'pending',
        });
    }
    return opportunities;
}
// ---------------------------------------------------------------------------
// 4. Progressive Disclosure — Detection Confidence
// ---------------------------------------------------------------------------
/**
 * Returns a 0-1 score indicating how confident the system is in its
 * entity detection, based on the number of connected data sources.
 */
export function getDetectionConfidence(connectedSourceCount) {
    // 1 source → 0.25, 2 → 0.5, 3 → 0.7, 4 → 0.85, 5+ → 0.95
    const curve = [0, 0.25, 0.5, 0.7, 0.85, 0.95];
    const idx = Math.min(connectedSourceCount, curve.length - 1);
    return curve[idx];
}
//# sourceMappingURL=cold-start.js.map